import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import { SURFACE } from '../../mesh/attributes';
import { PATTERN } from '../../../render/patterns/patternIds';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import { ringPoints } from '../solids';
import { buildConventEnclosure, conventEnclosureRings } from './conventEnclosure';
import { buildConventRange, conventRangeRect } from './conventRange';
import { buildConventTrees } from './conventTrees';
import { corners } from './genericBlock';
import type { LandmarkContext } from './landmarkFeature';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import {
  buildPaulusbroederskapel,
  paulusbroedersObstacles,
  paulusbroedersPlan,
} from './paulusbroeders';
import reading from './paulusbroedersPlan.json';
import { meshFrameOf, toLayout, type PlanFrame } from './planFrame';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

export function paulusRangeFrame(
  plan: PlanFrame,
  part: (typeof reading.ranges)[number]
): PlanFrame {
  return {
    origin: toLayout(plan, [part.u, part.v]),
    heading: plan.heading + (part.angleDeg * Math.PI) / 180,
    ring: [],
  };
}

export const paulusTreePoints = (): Vec2[] => reading.orchardTrees.map((p) => [p[0]!, p[1]!]);

/** Foundations support actual chapel and range walls, never the whole orchard reservation. */
export function paulusConventFoundations(landmark: Landmark): Ring[] {
  const { plan } = paulusbroedersPlan(landmark);
  return [
    ...paulusbroedersObstacles(landmark),
    ...reading.ranges.map((part) =>
      corners(conventRangeRect(part)).map((p) => toLayout(paulusRangeFrame(plan, part), p))
    ),
  ];
}

export function paulusConventObstacles(landmark: Landmark): Ring[] {
  const { plan } = paulusbroedersPlan(landmark);
  return [
    ...paulusConventFoundations(landmark),
    ...conventEnclosureRings(reading.walls).map((ring) => ring.map((p) => toLayout(plan, p))),
    ...paulusTreePoints().map((p) => ringPoints(p, 0.15, 5, 0).map((p) => toLayout(plan, p))),
  ];
}

/** Separate source-read bodies and fruit-tree ground, with the northern canal houses retained. */
export function buildPaulusbroedersConvent(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & { timber: LinearRGB; foliage: LinearRGB },
  ids: { readonly edgeId: number; readonly seed: number },
  tracery: TracerySink = NO_TRACERY,
  context: Pick<LandmarkContext, 'elevation' | 'elevationAt'> = {}
): void {
  buildPaulusbroederskapel(builder, landmark, colors, ids, tracery);
  const { plan, frame } = paulusbroedersPlan(landmark),
    styles = paintedChapelStyles(colors, ids, tracery),
    brick = { ...styles, wall: { ...styles.wall, surface: SURFACE.brick } },
    base = context.elevation ?? 0,
    terrain = context.elevationAt ?? (() => base),
    localGround = (p: Vec2) => terrain(toLayout(plan, p)) - base;
  for (const part of reading.ranges)
    buildConventRange(builder, meshFrameOf(paulusRangeFrame(plan, part)), brick, part);
  buildConventEnclosure(builder, brick.wall, frame, localGround, reading.walls);
  const treeStyle = { ...brick.wall, pattern: PATTERN.none };
  buildConventTrees(
    builder,
    treeStyle,
    treeStyle,
    frame,
    colors,
    localGround,
    paulusTreePoints(),
    'paulusbroeders'
  );
}
